import { lazy, Suspense } from 'react'
import 'react-quill-new/dist/quill.snow.css'
import Hls from 'hls.js'
import { getFirstThreeWords } from 'src/utils/string-formatter.util'

// For more info see : https://github.com/slab/quill/issues/2044#issuecomment-1387091319
const ReactQuillLazy = lazy(async () => {
  const ReactQuill = (await import('react-quill-new')).default

  const quill = ReactQuill.Quill
  const Video = quill.import('formats/video') as any
  const Link = quill.import('formats/link') as any
  const Header = quill.import('formats/header') as any
  const Embed = quill.import('blots/block/embed') as any

  // @ts-ignore
  const ImageResize = (await import('quill-image-resize-module-react')).default
  quill.register('modules/imageResize', ImageResize)

  class CustomVideo extends Video {
    static blotName = 'custom-video'
    static className = 'ql-video'
    static tagName = 'DIV'

    static create(videoUrl: string) {
      if (videoUrl.includes('youtube')) {
        const url = CustomVideo.sanitizeYoutubeUrl(videoUrl)
        const node = document.createElement('iframe')
        return CustomVideo.createYoutubeVideo(node, url)
      } else {
        const url = CustomVideo.sanitize(videoUrl)
        const node = document.createElement('div')
        return CustomVideo.createCustomVideo(node, url)
      }
    }

    static createCustomVideo(node: any, url: string) {
      const video = document.createElement('video')
      video.setAttribute('controls', '')
      video.setAttribute('type', 'video/mp4')
      video.setAttribute('preload', 'metadata')
      if (Hls.isSupported()) {
        const hls = new Hls()
        hls.loadSource(url)
        hls.attachMedia(video)
      }
      // hls.js is not supported on platforms that do not have Media Source Extensions (MSE) enabled.
      // When the browser has built-in HLS support (check using `canPlayType`), we can provide an HLS manifest (i.e. .m3u8 URL) directly to the video element through the `src` property.
      // This is using the built-in support of the plain video element, without using hls.js.
      else if (video.canPlayType('application/vnd.apple.mpegurl')) {
        video.src = url
        video.addEventListener('canplay', function () {
          video.play()
        })
      } else {
        video.setAttribute('src', url)
      }

      node.appendChild(video)

      return node
    }

    static createYoutubeVideo(node: any, videoUrl: string) {
      const video = document.createElement('video')
      video.setAttribute('controls', 'true')
      video.setAttribute('type', 'video/mp4')
      video.setAttribute('style', 'height: 200px; width: 100%')
      video.setAttribute('src', videoUrl)
      node.appendChild(video)

      return node
    }

    static sanitize(url: string) {
      return Link.sanitize ? Link.sanitize(url) : url
    }

    static sanitizeYoutubeUrl(url: string) {
      const getId = (url: string) => {
        const regExp = /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|&v=)([^#&?]*).*/
        const match = url.match(regExp)

        return match && match[2]?.length === 11 ? match[2] : null
      }

      const embedUrl = `https://www.youtube.com/embed/${getId(url)}`
      return Link.sanitize ? Link.sanitize(embedUrl) : embedUrl
    }
  }
  quill.register('formats/video', CustomVideo)

  class Hr extends Embed {
    static blotName = 'hr'
    static tagName = 'hr'

    static create() {
      const node = document.createElement('hr')
      return node
    }
  }
  quill.register('formats/hr', Hr)

  class MyLink extends Link {
    static blotName = 'link'
    static tagName = 'A'

    static create(value: any) {
      const node = document.createElement('a')
      const sanitizedValue = Link.sanitize ? Link.sanitize(value) : value
      node.setAttribute('href', sanitizedValue)
      if (sanitizedValue.startsWith('#')) {
        node.removeAttribute('target')
      } else {
        node.setAttribute('target', '_blank')
      }
      return node
    }

    format(name: any, value: any) {
      if (name !== 'link' || !value) {
        return super.format(name, value)
      }

      const sanitizedValue = Link.sanitize ? Link.sanitize(value) : value
      if (sanitizedValue.startsWith('#')) {
        ;(this as any).domNode?.removeAttribute('target')
      } else {
        ;(this as any).domNode?.setAttribute('target', '_blank')
      }
      super.format(name, sanitizedValue)
    }
  }
  quill.register('formats/link', MyLink)

  class MyHeader extends Header {
    static blotName = 'header'
    static tagName = ['H1', 'H2', 'H3', 'H4', 'H5', 'H6']

    static formats(domNode: Element) {
      const text = domNode.textContent
      const id = domNode.getAttribute('id')
      if (!id && text) {
        domNode.setAttribute('id', getFirstThreeWords(text))
      }
      return MyHeader.tagName.indexOf(domNode.tagName) + 1
    }
  }
  quill.register('formats/header', MyHeader)

  return {
    default: ({ forwardedRef, ...rest }: any) => <ReactQuill ref={forwardedRef} {...rest} />,
  }
})

export const ReactQuillDynamic = (props: any) => (
  <Suspense fallback={<div className="loading" />}>
    <ReactQuillLazy {...props} />
  </Suspense>
)
